<template>
    <transition name="ebc-side" appear>
        <div v-if="show" class="ebc-side-panel">
            <div class="ebc-side-panel__overlay" @click="closeOnOverlay && $emit('close')"></div>
            <aside
                class="ebc-side-panel__sheet"
                :class="`is-${size}`"
                role="dialog"
                aria-modal="true"
                :aria-label="title"
            >
                <header class="ebc-side-panel__head">
                    <div class="ebc-side-panel__titles">
                        <h4 class="ebc-side-panel__title">{{ title }}</h4>
                        <span v-if="subtitle" class="ebc-side-panel__sub">{{ subtitle }}</span>
                    </div>
                    <div class="ebc-side-panel__actions">
                        <slot name="actions" />
                        <button type="button" class="ebc-side-panel__close" aria-label="닫기" @click="$emit('close')">
                            <i class="fas fa-times" aria-hidden="true"></i>
                        </button>
                    </div>
                </header>
                <div class="ebc-side-panel__body">
                    <slot />
                </div>
                <footer v-if="$slots.footer" class="ebc-side-panel__foot">
                    <slot name="footer" />
                </footer>
            </aside>
        </div>
    </transition>
</template>

<script>
/**
 * 에너지 요금 관리 모달 공통 사이드바.
 * 크기: default(640px) / wide(1040px), 화면이 좁으면 90vw 안으로 줄어든다.
 */
export default {
    name: "SettingsSidePanel",
    props: {
        show: { type: Boolean, default: false },
        title: { type: String, default: "" },
        subtitle: { type: String, default: "" },
        size: {
            type: String,
            default: "default",
            validator: (v) => ["default", "wide"].includes(v),
        },
        closeOnOverlay: { type: Boolean, default: true },
    },
    watch: {
        show: {
            immediate: true,
            handler(visible) {
                if (visible) document.addEventListener("keydown", this.onKeydown);
                else document.removeEventListener("keydown", this.onKeydown);
            },
        },
    },
    beforeDestroy() {
        document.removeEventListener("keydown", this.onKeydown);
    },
    methods: {
        onKeydown(event) {
            // sweetalert 등 위에 뜬 대화상자가 Esc 를 먼저 처리한다
            if (event.key !== "Escape" || document.querySelector(".swal2-container")) return;
            this.$emit("close");
        },
    },
};
</script>

<style scoped>
.ebc-side-panel {
    position: fixed;
    inset: 0;
    z-index: var(--ebc-side-z);
}

.ebc-side-panel__overlay {
    position: absolute;
    inset: 0;
    background: rgba(19, 32, 58, 0.28);
}

.ebc-side-panel__sheet {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    width: var(--ebc-side-w);
    display: flex;
    flex-direction: column;
    background: var(--ebc-surface);
    box-shadow: -8px 0 32px rgba(19, 32, 58, 0.16);
    color: var(--ebc-text);
}

.ebc-side-panel__sheet.is-wide {
    width: var(--ebc-side-w-wide);
}

.ebc-side-panel__head {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 16px 20px;
    border-bottom: 1px solid var(--ebc-hairline-strong);
}

.ebc-side-panel__titles {
    display: flex;
    align-items: baseline;
    gap: 8px;
    min-width: 0;
}

.ebc-side-panel__title {
    margin: 0;
    font-size: 15px;
    font-weight: 700;
    color: var(--ebc-text);
    letter-spacing: -0.01em;
    white-space: nowrap;
}

.ebc-side-panel__sub {
    overflow: hidden;
    font-size: 12px;
    color: var(--ebc-text-muted);
    white-space: nowrap;
    text-overflow: ellipsis;
}

.ebc-side-panel__actions {
    display: flex;
    align-items: center;
    gap: 8px;
}

.ebc-side-panel__close {
    width: 28px;
    height: 28px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: none;
    border-radius: var(--ebc-radius-pill);
    background: transparent;
    color: var(--ebc-text-sub);
    font-size: 14px;
    cursor: pointer;
}

.ebc-side-panel__close:hover {
    background: var(--ebc-track);
    color: var(--ebc-text);
}

.ebc-side-panel__close:focus {
    outline: none;
}

.ebc-side-panel__body {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 20px;
}

.ebc-side-panel__foot {
    flex-shrink: 0;
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 12px 20px;
    border-top: 1px solid var(--ebc-hairline-strong);
    background: var(--ebc-surface-sunken);
}

.ebc-side-enter-active,
.ebc-side-leave-active,
.ebc-side-enter-active .ebc-side-panel__sheet,
.ebc-side-leave-active .ebc-side-panel__sheet {
    transition:
        opacity 0.25s var(--ebc-ease),
        transform 0.25s var(--ebc-ease);
}

.ebc-side-enter,
.ebc-side-leave-to {
    opacity: 0;
}

.ebc-side-enter .ebc-side-panel__sheet,
.ebc-side-leave-to .ebc-side-panel__sheet {
    transform: translateX(100%);
}
</style>
